function selectBudgetProducts(products, selection) {
  const requestedCount = Number(selection?.requestedCount);
  const maxTotalMinor = Number(selection?.maxTotalMinor);
  const maxItemMinor = selection?.maxItemMinor == null ? null : Number(selection.maxItemMinor);
  if (selection?.currency !== 'EGP' || !Number.isInteger(requestedCount) || requestedCount < 1 || requestedCount > 5 || !Number.isInteger(maxTotalMinor) || maxTotalMinor < 1) {
    return { feasible: false, products: [], totalMinor: null, minimumTotalMinor: null };
  }
  const seen = new Set();
  const valid = products
    .filter((product) => product && typeof product.handle === 'string')
    .filter((product) => Number.isInteger(product.priceMinor) && product.priceMinor >= 0 && (maxItemMinor == null || product.priceMinor <= maxItemMinor))
    .filter((product) => !seen.has(product.handle) && seen.add(product.handle))
    .sort((a, b) => Number(a.rank ?? 999) - Number(b.rank ?? 999));
  if (valid.length < requestedCount) return { feasible: false, products: [], totalMinor: null, minimumTotalMinor: null };
  let minimumTotalMinor = null;
  let best = null;
  const visit = (start, chosen, totalMinor) => {
    if (chosen.length === requestedCount) {
      if (selection.distinctCategories && new Set(chosen.map((product) => product.category || product.handle)).size !== requestedCount) return;
      if (minimumTotalMinor === null || totalMinor < minimumTotalMinor) minimumTotalMinor = totalMinor;
      if (totalMinor > maxTotalMinor) return;
      const preferredCount = chosen.filter((product) => product.preferred).length;
      const rankScore = chosen.reduce((sum, product) => sum + Number(product.rank ?? 999), 0);
      const diversity = new Set(chosen.map((product) => product.category).filter(Boolean)).size;
      if (!best || preferredCount > best.preferredCount
        || (preferredCount === best.preferredCount && diversity > best.diversity)
        || (preferredCount === best.preferredCount && diversity === best.diversity && rankScore < best.rankScore)
        || (preferredCount === best.preferredCount && diversity === best.diversity && rankScore === best.rankScore && totalMinor < best.totalMinor)) {
        best = { products: [...chosen], totalMinor, preferredCount, rankScore, diversity };
      }
      return;
    }
    for (let index = start; index <= valid.length - (requestedCount - chosen.length); index++) {
      visit(index + 1, [...chosen, valid[index]], totalMinor + valid[index].priceMinor);
    }
  };
  visit(0, [], 0);
  return best
    ? { feasible: true, products: best.products, totalMinor: best.totalMinor, minimumTotalMinor }
    : { feasible: false, products: [], totalMinor: null, minimumTotalMinor };
}

class JamilaAIAdvisor extends HTMLElement {
  connectedCallback() {
    this.endpoint = this.dataset.endpoint;
    this.form = this.querySelector('[data-form]');
    if (!this.endpoint || !this.form) return;
    this.input = this.querySelector('[data-input]');
    this.conversation = this.querySelector('[data-conversation]');
    this.status = this.querySelector('[data-status]');
    this.send = this.querySelector('[data-send]');
    this.reset = this.querySelector('[data-reset]');
    this.examples = this.querySelector('[data-examples]');
    this.history = [];
    this.requestId = 0;
    this.abortController = null;
    this.form.addEventListener('submit', (event) => { event.preventDefault(); this.submit(); });
    this.input.addEventListener('keydown', (event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); this.submit(); } });
    this.input.addEventListener('input', () => this.resizeInput());
    this.reset.addEventListener('click', () => this.startOver());
    this.examples.addEventListener('click', (event) => { const button = event.target.closest('[data-example]'); if (button) { this.input.value = button.dataset.example; this.resizeInput(); this.input.focus(); } });
    this.resizeInput();
  }

  startOver() {
    this.requestId++;
    this.abortController?.abort();
    this.abortController = null;
    this.history = [];
    this.conversation.replaceChildren();
    const welcome = document.createElement('div'); welcome.className = 'jamila-advisor__welcome';
    const icon = document.createElement('span'); icon.setAttribute('aria-hidden', 'true'); icon.textContent = '✧';
    const title = document.createElement('h2'); title.textContent = 'What can I help you find?';
    const text = document.createElement('p'); text.textContent = 'You can write in English or Arabic. Start with what you need; I’ll ask if a detail is missing.';
    welcome.append(icon, title, text); this.conversation.append(welcome);
    this.reset.hidden = true; this.examples.hidden = false; this.status.textContent = ''; this.input.value = ''; this.input.disabled = false; this.send.disabled = false; this.resizeInput(); this.input.focus();
  }

  resizeInput() {
    this.input.style.height = 'auto';
    this.input.style.height = `${Math.min(this.input.scrollHeight, 120)}px`;
  }

  addBubble(content, role) {
    const bubble = document.createElement('div');
    bubble.className = `jamila-advisor__bubble jamila-advisor__bubble--${role}`;
    bubble.textContent = content;
    if (/[\u0600-\u06ff]/.test(content)) bubble.dir = 'rtl';
    this.conversation.append(bubble);
    this.conversation.scrollTop = this.conversation.scrollHeight;
    return bubble;
  }

  async validateClientProduct(product) {
    if (!product.needsClientValidation) return { ...product, priceMinor: Number.isFinite(product.priceEGP) ? Math.round(product.priceEGP * 100) : null };
    if (typeof product.handle !== 'string' || !/^[a-z0-9][a-z0-9-]{0,200}$/.test(product.handle)) return null;
    try {
      const response = await fetch(`/products/${product.handle}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(6000) });
      if (!response.ok) return null;
      const live = await response.json();
      if (live.handle !== product.handle || !live.available || !Array.isArray(live.variants)) return null;
      const variant = live.variants.find((item) => item.available);
      if (!variant) return null;
      if (!this.currencyPromise) this.currencyPromise = fetch('/cart.js', { credentials: 'same-origin', signal: AbortSignal.timeout(6000) }).then((r) => r.ok ? r.json() : null).then((cart) => cart?.currency || null).catch(() => null);
      const currency = await this.currencyPromise;
      if (!currency) this.currencyPromise = null;
      const rawImage = live.featured_image || live.images?.[0] || '';
      const image = rawImage.startsWith('//') ? `https:${rawImage}` : rawImage;
      const priceMinor = currency === 'EGP' && Number.isInteger(Number(variant.price)) ? Number(variant.price) : null;
      return { ...product, title: String(live.title || '').slice(0, 180), url: `https://jamila-eg.net/products/${product.handle}?variant=${encodeURIComponent(variant.id)}`, image,
        priceMinor, priceEGP: priceMinor == null ? null : priceMinor / 100, variantId: variant.id,
        needsClientValidation: false };
    } catch { return null; }
  }

  async addProducts(products, selection = null, requestId = this.requestId) {
    const list = document.createElement('div'); list.className = 'jamila-advisor__products';
    let count = 0;
    const validated = (await Promise.all(products.slice(0, selection ? 18 : 3).map((proposal) => this.validateClientProduct(proposal)))).filter(Boolean);
    if (requestId !== this.requestId) return { count: 0, budget: null };
    const budget = selection ? selectBudgetProducts(validated, selection) : null;
    const displayProducts = budget ? budget.products : validated;
    for (const product of displayProducts) {
      if (!product) continue;
      let url;
      try { url = new URL(product.url); } catch { continue; }
      if (url.origin !== 'https://jamila-eg.net' || !url.pathname.startsWith('/products/')) continue;
      const link = document.createElement('a'); link.className = 'jamila-advisor__product'; link.href = url.href;
      const image = document.createElement('img');
      if (typeof product.image === 'string' && /^https:\/\/(?:cdn\.shopify\.com|jamila-eg\.net)\//.test(product.image)) image.src = product.image;
      image.alt = product.title || ''; image.loading = 'lazy'; image.width = 78; image.height = 84;
      const content = document.createElement('div');
      const title = document.createElement('strong'); title.textContent = product.title || 'View product';
      const reason = document.createElement('small'); reason.textContent = product.reason || '';
      const caution = document.createElement('small'); caution.textContent = product.caution || '';
      content.append(title, reason, caution);
      if (typeof product.priceEGP === 'number' && Number.isFinite(product.priceEGP)) {
        const price = document.createElement('em'); price.textContent = `${new Intl.NumberFormat('en-EG', { maximumFractionDigits: 2 }).format(product.priceEGP)} EGP`; content.append(price);
      }
      link.append(image, content); list.append(link); count++;
    }
    if (count) this.conversation.append(list);
    this.conversation.scrollTop = this.conversation.scrollHeight;
    return { count, budget };
  }

  budgetNote(selection, result, language) {
    if (!selection || !result) return '';
    const money = (minor) => `${new Intl.NumberFormat('en-EG', { maximumFractionDigits: 2 }).format(minor / 100)} EGP`;
    const itemLabel = language === 'ar' ? (selection.requestedCount === 1 ? 'منتج' : 'منتجات') : (selection.requestedCount === 1 ? 'product' : 'products');
    const suitableLabel = selection.requestedCount === 1 ? 'مناسب' : 'مناسبة';
    const confirmPronoun = selection.requestedCount === 1 ? 'إنه' : 'إنهم';
    if (result.feasible) {
      const limit = Number.isInteger(selection.maxItemMinor)
        ? (language === 'ar' ? `، وكل منتج لا يتجاوز ${money(selection.maxItemMinor)}` : `, with every product at or below ${money(selection.maxItemMinor)}`)
        : '';
      return language === 'ar'
        ? `بعد التحقق من الأسعار الحالية: ${selection.requestedCount} ${itemLabel} بإجمالي ${money(result.totalMinor)}${limit}. الشحن وأي تغير في السعر بيظهروا نهائياً في Checkout.`
        : `After checking current prices: ${selection.requestedCount} ${itemLabel} total ${money(result.totalMinor)}${limit}. Shipping and any price changes are confirmed at checkout.`;
    }
    if (Number.isInteger(result.minimumTotalMinor)) return language === 'ar'
      ? `بعد التحقق من الأسعار الحالية، أقل إجمالي وجدته لـ ${selection.requestedCount} ${itemLabel} ${suitableLabel} هو ${money(result.minimumTotalMinor)}، وده أعلى من ميزانيتك ${money(selection.maxTotalMinor)}. تحبي أقلل العدد ولا نزود الميزانية؟`
      : `After checking current prices, the lowest total I found for ${selection.requestedCount} suitable ${itemLabel} is ${money(result.minimumTotalMinor)}, above your ${money(selection.maxTotalMinor)} budget. Would you like fewer products or a higher budget?`;
    return language === 'ar'
      ? `بعد التحقق من التوفر والأسعار، ملقتش ${selection.requestedCount} ${itemLabel} ${suitableLabel} أقدر أؤكد ${confirmPronoun} داخل ميزانيتك حالياً. تحبي أقلل العدد أو نغيّر نوع المنتجات؟`
      : `After checking availability and prices, I could not confirm ${selection.requestedCount} suitable ${itemLabel} within your budget. Would you like fewer products or a different product type?`;
  }

  async submit() {
    const message = this.input.value.trim();
    if (!message || this.send.disabled) return;
    if (message.length > 1500) { this.status.textContent = 'Please shorten your question.'; return; }
    const requestId = ++this.requestId;
    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;
    let timedOut = false;
    const timeoutId = setTimeout(() => { timedOut = true; controller.abort(); }, 15000);
    this.conversation.querySelector('.jamila-advisor__welcome')?.remove();
    this.addBubble(message, 'user');
    this.input.value = ''; this.resizeInput(); this.input.disabled = true; this.send.disabled = true;
    this.reset.hidden = false; this.examples.hidden = true; this.status.textContent = /[\u0600-\u06ff]/.test(message) ? 'جميلة بتفكر…' : 'Jamila is thinking…';
    try {
      const response = await fetch(this.endpoint, { method: 'POST', mode: 'cors', credentials: 'omit', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message, history: this.history.slice(-12) }), signal: controller.signal });
      let data = {};
      try { data = await response.json(); } catch { data = {}; }
      if (requestId !== this.requestId) return;
      if (!response.ok) throw new Error(response.status === 429
        ? (/[\u0600-\u06ff]/.test(message) ? 'وصلتي لحد الرسائل مؤقتاً. استني شوية وحاولي تاني أو تواصلي مع جميلة.' : 'You have reached the message limit. Please wait and try again, or contact Jamila.')
        : (/[\u0600-\u06ff]/.test(message) ? 'الاستشاري غير متاح مؤقتاً. حاولي مرة تانية أو تواصلي مع جميلة.' : 'The advisor is unavailable. Please try again or contact Jamila.'));
      const answer = typeof data.message === 'string' ? data.message : 'I could not prepare an answer. Please try again.';
      const replyBubble = this.addBubble(answer, 'assistant');
      const pending = Array.isArray(data.products) && data.products.some((product) => product.needsClientValidation);
      if (pending) this.status.textContent = /[\u0600-\u06ff]/.test(message) ? 'بتأكد من توفر المنتجات…' : 'Checking live product availability…';
      const productResult = Array.isArray(data.products) ? await this.addProducts(data.products, data.selection || null, requestId) : { count: 0, budget: null };
      if (requestId !== this.requestId) return;
      const language = /[\u0600-\u06ff]/.test(message) ? 'ar' : 'en';
      const localNote = data.selection
        ? this.budgetNote(data.selection, productResult.budget, language)
        : data.kind === 'recommendation' && !productResult.count
          ? (language === 'ar' ? 'الاقتراحات دي مش متاحة حالياً، لكن تقدري تتواصلي مع جميلة علشان نساعدك تختاري بديل.' : 'Those suggestions are not available right now. You can contact Jamila for an alternative.')
          : '';
      if (localNote) this.addBubble(localNote, 'assistant');
      const followUp = data.selection ? '' : (typeof data.followUp === 'string' ? data.followUp : '');
      if (followUp) this.addBubble(followUp, 'assistant');
      this.history.push({ role: 'user', content: message }, { role: 'assistant', content: [replyBubble.textContent, localNote, followUp].filter(Boolean).join('\n') });
      this.history = this.history.slice(-12);
      this.status.textContent = '';
    } catch (error) {
      if (requestId !== this.requestId) return;
      this.status.textContent = timedOut
        ? (/[\u0600-\u06ff]/.test(message) ? 'الرد أخد وقت أطول من المتوقع. حاولي مرة تانية أو تواصلي مع جميلة.' : 'This is taking too long. Please try again or contact Jamila.')
        : error.message;
      this.input.value = message;
      this.resizeInput();
    } finally {
      clearTimeout(timeoutId);
      if (requestId === this.requestId) { this.abortController = null; this.input.disabled = false; this.send.disabled = false; this.input.focus(); }
    }
  }
}
customElements.define('jamila-ai-advisor', JamilaAIAdvisor);
